import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Form,
  Input,
  Checkbox,
  Button,
  Row,
  Col,
  Grid,

  Modal,
  Typography,
  Alert,
  Progress,
} from "antd";

import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useNavigation } from "react-router-dom";
import { loginUser, resetOwner, setAccountPW, setAuthToken } from "../apiBridge";

import logo from "../assets/new_vtkd_logo.png";
import logopattern from "../assets/VTKDBG4.png";

const zxcvbn = require("zxcvbn");

function getLoginErrorMessage(error: string) {
  if (error === "temporary password expired") {
    return "Temporary password expired. Please request a new password reset.";
  }
  if (error === "no organization") {
    return error;
  }
  return "Incorrect username or password";
}

function getSetPasswordErrorMessage(error: string) {
  if (error === "please specify a new password") {
    return "New password must be different from your temporary password.";
  }
  if (error === "temporary password expired") {
    return "Your temporary password has expired. Please go back to login and request a new password reset.";
  }
  if (error === "password too weak") {
    return "Password is too weak. Please choose a stronger password.";
  }
  if (
    error === "incorrect password" ||
    error === "incorrect password or email"
  ) {
    return "Incorrect current password.";
  }
  if (error === "invalid input parameters") {
    return "Password does not meet the requirements.";
  }
  if (error === "account not found") {
    return "Account not found.";
  }
  return "Invalid password or new password";
}

const LoginPage = () => {
  const { useBreakpoint } = Grid;
  const { Title, Paragraph, Link, Text } = Typography;
  const screens = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = useNavigation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const from = params.get("from");

  const [showPage, setShowPage] = useState<
    "login" | "forgotpwowner" | "setaccountpw"
  >("login");
  const [showMemberForgotPWModal, setShowMemberForgotPWModal] = useState(false);
  const [showEmailSentAlert, setShowEmailSentAlert] = useState(false);
  const [showSuccessSetPW, setShowSuccessSetPW] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [showErrorLoginAlert, setShowErrorLoginAlert] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState("");
  const [passwordResetErrorMessage, setPasswordResetErrorMessage] = useState("");
  const [passwordScore, setPasswordScore] = useState(0);
  const [newPW, setNewPW] = useState("");
  const [pendingUser, setPendingUser] = useState<{
    username: string;
    password: string;
    email: string;
  }>({username: "", password: "", email: ""});

  const [form] = Form.useForm();

  const resetLoginPageAlert = useCallback(() => {
    setShowEmailSentAlert(false);
    setShowSuccessSetPW(false);
    setShowErrorLoginAlert(false);
    setLoginErrorMessage("");
    setPasswordResetErrorMessage("");
  }, []);

  const onFinishLogin = useCallback(async (values: any) => {
    setLoadingLogin(true);
    // 判断是否为邮箱格式来确定登录模式
    const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(values.username);
    const loginMode = isEmail ? "owner" : "member";
    
    const res = await loginUser({
      username: values.username,
      email: isEmail ? values.username : "",
      password: values.password,
      mode: loginMode,
    });
    const data = await res.json();
    setLoadingLogin(false);
    resetLoginPageAlert();

    if (data.error) {
      setLoginErrorMessage(getLoginErrorMessage(data.error));
      setShowErrorLoginAlert(true);
    } else if (res.status === 202) {
      setShowErrorLoginAlert(false);
      setPendingUser({
        email: isEmail ? values.username : '',
        username: values.username,
        password: values.password,
      });

      setNewPW("");
      setPasswordScore(0);
      setShowPage("setaccountpw");
    } else if (res.status === 200) {
      const token = data.token?.access_token || data.token;
      const expiresIn = Number(data.expires_in ?? data.token?.expires_in);
      setAuthToken(token, expiresIn);
      const role = data.user_info?.role || (isEmail ? "Owner" : "User");
      navigate(from || role === "User" ? "/" : "/users");
    } else {
      console.error("Unhandled Status Code: ", res.status, data);
    }
  }, [navigate, from, resetLoginPageAlert]);

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };



  const onRequestPWReset = async (values: any) => {
    setLoadingLogin(true);
    setShowEmailSentAlert(false);
    setPasswordResetErrorMessage("");

    try {
      const res = await resetOwner(values);
      if (!res.ok) {
        const data = await res.json();
        setPasswordResetErrorMessage(
          data.code === "no_organization"
            ? "This account cannot request for a password reset through the Portal. Please check the account."
            : "Unable to request password reset. Please check your connection or try again later."
        );
        return;
      }
      setShowEmailSentAlert(true);
    } catch (error) {
      console.error("Password reset request failed:", error);
      setPasswordResetErrorMessage(
        "Unable to request password reset. Please check your connection or try again later."
      );
    } finally {
      setLoadingLogin(false);
    }
  };

  const onFinishSetPW = async (values: any) => {
    setLoadingLogin(true);
    resetLoginPageAlert()

    const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(pendingUser.username);
    const res = await setAccountPW({
      email: isEmail ? pendingUser.username : "",
      username: pendingUser.username,
      password: pendingUser.password,
      new_password: values.retypepassword,
      mode: isEmail ? "owner" : "member",
    });
    const data = await res.json();
    setLoadingLogin(false);
    if (data.error) {
      setLoginErrorMessage(getSetPasswordErrorMessage(data.error));
      setShowErrorLoginAlert(true);
    } else if (res.status === 200) {
      form.setFieldValue("password", "");
      setPendingUser({ username: "", password: "", email: "" });
      setShowSuccessSetPW(true);
      setShowPage("login");
    } else {
      console.error("Unhandled Status Code: ", res.status, data);
    }
  };



  const resetLoginPage = () => {
    setShowPage("login");
    resetLoginPageAlert()
    setPendingUser({ username: "", password: "", email: "" });
  };

  const autoLoginKeyRef = useRef("");
  
  useEffect(() => {
    if (location.pathname !== "/login" && location.pathname !== "/reset") {
      return;
    }

    const inputEmail = params.get("email")?.trim();
    const inputPwd = params.get("passphrase");
    if (!inputEmail || !inputPwd) {
      return;
    }

    form.setFieldsValue({
      username: inputEmail,
      password: inputPwd,
      remember: true,
    });

    const autoLoginKey = `${location.pathname}\x00${inputEmail}\x00${inputPwd}`;
    if (autoLoginKeyRef.current === autoLoginKey) {
      return;
    }
    autoLoginKeyRef.current = autoLoginKey;

    onFinishLogin({
      username: inputEmail,
      password: inputPwd,
    });
  }, [form, location.pathname, params, onFinishLogin])

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        height: "100vh",
        flexDirection: "column",
        alignItems: "center",
        backgroundImage: `url(${logopattern})`,
        backgroundColor: "#f7f7f7",
        backgroundSize: 166,
      }}
    >
      <Modal
        width={472}
        title={null}
        open={showMemberForgotPWModal}
        maskClosable={false}
        centered
        okText="Back to Login"
        closeIcon={null}
        footer={
          <Button
            key="ok"
            type="primary"
            onClick={() => setShowMemberForgotPWModal(false)}
          >
            Back to Login
          </Button>
        }
      >
        <Title level={3}>Contact your Organization</Title>
        <Paragraph>
          Members of an organization have to contact their organization to reset
          their password.
        </Paragraph>
      </Modal>

      <div
        style={{
          boxShadow: "0 0 8px 0 #cbcbcb",
          borderRadius: 4,
          width: screens.md ? 472 : "calc(100% - 10px)",
          padding: 34,
          background: "white",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <img src={logo} alt="vtkd_logo" style={{ height: 47 }} />
        </div>

        {/* LOGIN FORM */}
        {showPage === "login" && (
          <div>
            {showSuccessSetPW && (
              <Alert
                message="Password updated successfully. Please login again."
                type="success"
                showIcon
              />
            )}
            {showErrorLoginAlert && (
              <Alert
                message={loginErrorMessage || "Incorrect username or password"}
                type="error"
                showIcon
              />
            )}
            <Form
              form={form}
              name="loginform"
              layout="vertical"
              initialValues={{ remember: true }}
              onFinish={onFinishLogin}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <Form.Item
                label={screens.md ? "Username" : ""}
                name="username"
                rules={[
                  { required: true, message: "Please input your username or email!" },
                ]}
              >
                <Input placeholder="Username or Email" />
              </Form.Item>

              <Form.Item
                label={screens.md ? "Password" : ""}
                name="password"
                rules={[
                  { required: true, message: "Please input your password!" },
                ]}
              >
                <Input.Password placeholder="Password" />
              </Form.Item>
              <Row>
                <Col span={12}>
                  <Form.Item name="remember" valuePropName="checked">
                    <Checkbox>Remember Me</Checkbox>
                  </Form.Item>
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                  <Button
                    type="link"
                    onClick={() => {
                      const username = form.getFieldValue('username');
                      if (!username) {
                        // 如果用户未输入内容，默认显示owner的忘记密码表单
                        setShowPage("forgotpwowner");
                        return;
                      }
                      const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(username);
                      if (!isEmail) {
                        setShowMemberForgotPWModal(true);
                      } else {
                        setShowPage("forgotpwowner");
                      }
                    }}
                    disabled={loadingLogin || navigation.state === "loading"}
                  >
                    Forgot Password
                  </Button>
                </Col>
              </Row>

              <Form.Item style={{ margin: 0 }}>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  loading={loadingLogin || navigation.state === "loading"}
                >
                  Submit
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}

        {/* CHANGE PASSWORD OWNER */}
        {showPage === "forgotpwowner" && (
          <div>
            <Title level={2}>Forgot Password</Title>
            <Paragraph>
            Members of an organization have to contact their organization to reset their password.
            </Paragraph>
            <Paragraph>
              We will send you an with instructions on how to reset your
              password.
            </Paragraph>
            {passwordResetErrorMessage !== "" && (
              <Alert
                message={passwordResetErrorMessage}
                type="error"
                showIcon
                style={{ marginBottom: 20 }}
              />
            )}
            <Form
              name="forgotpwownerform"
              layout="vertical"
              initialValues={{ remember: true }}
              onFinish={onRequestPWReset}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
              validateTrigger="onSubmit"
            >
              <Form.Item
                label={screens.md ? "Email" : ""}
                name="email"
                rules={[
                  {},
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (
                        !value ||
                        !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                          value
                        )
                      ) {
                        return Promise.reject(
                          new Error("Please enter a valid email!")
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input placeholder="Email Address" />
              </Form.Item>

              <Form.Item>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  loading={loadingLogin}
                >
                  Request Password Reset
                </Button>
              </Form.Item>
            </Form>
            {showEmailSentAlert && (
              <Alert
                message="Request Received"
                description={
                  <>
                    <Paragraph>
                      If an account exists for this email address, password
                      reset instructions will be sent. If they do not arrive
                      soon, check your spam folder or submit the request again.
                    </Paragraph>
                    Need help?{" "}
                    <Link href="mailto:info@virtualtkd.gg">
                      Contact support
                    </Link>
                    .
                  </>
                }
                type="success"
                showIcon
              />
            )}
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <Button type="link" onClick={resetLoginPage}>
                Back to Login
              </Button>
            </div>
          </div>
        )}

        {/* SET ACCOUNT PASSWORD */}
        {showPage === "setaccountpw" && (
          <div>
            {showErrorLoginAlert && (
              <Alert
                message={loginErrorMessage || "Invalid password or new password"}
                type="error"
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}
            <Title level={2}>Set a new password</Title>
            <Paragraph>
              Protect your account with a unique password of at least 8
              characters long.
            </Paragraph>
            <Form
              name="setpwmemberform"
              layout="vertical"
              initialValues={{ accepttnc: true }}
              onFinish={onFinishSetPW}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <Form.Item
                label={screens.md ? "New Password" : ""}
                name="password"
                rules={[
                  {},
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const score = zxcvbn(value).score;
                      setNewPW(value);
                      setPasswordScore(score);
                      if (
                        !value ||
                        value.length < 8 ||
                        !/^(?=.*[a-zA-Z])(?=.*[0-9]).+$/.test(value)
                      ) {
                        return Promise.reject();
                      } else if (score < 2) {
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <div>
                  <Input.Password placeholder="New Password" />
                  <Progress
                    percent={Math.max(passwordScore * 25, 1)}
                    showInfo={false}
                    strokeColor={
                      passwordScore * 25 < 50
                        ? "#ff4d4f"
                        : passwordScore * 25 < 75
                        ? "#ff9e07"
                        : "#00b7b2"
                    }
                  />
                  <div style={{ marginBottom: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Strength:{" "}
                      <Text type="secondary" style={{ fontSize: "inherit" }}>
                        {passwordScore === 0
                          ? "VERY WEAK"
                          : passwordScore === 1
                          ? "VERY WEAK"
                          : passwordScore === 2
                          ? "WEAK"
                          : passwordScore === 3
                          ? "FAIR"
                          : "STRONG"}
                      </Text>
                    </Text>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Your password must include:
                    </Text>
                  </div>
                  <div>
                    {/^(?=.*[a-zA-Z]).+$/.test(newPW) ? (
                      <CheckCircleOutlined style={{ color: "#00b7b2" }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                    )}
                    &nbsp;&nbsp;
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      At least 1 letter
                    </Text>
                  </div>
                  <div>
                    {/^(?=.*[0-9]).+$/.test(newPW) ? (
                      <CheckCircleOutlined style={{ color: "#00b7b2" }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                    )}
                    &nbsp;&nbsp;
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      At least 1 number
                    </Text>
                  </div>
                  <div>
                    {newPW.length >= 8 ? (
                      <CheckCircleOutlined style={{ color: "#00b7b2" }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                    )}
                    &nbsp;&nbsp;
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Minimum of 8 characters
                    </Text>
                  </div>
                  <div>
                    {passwordScore > 1 ? (
                      <CheckCircleOutlined style={{ color: "#00b7b2" }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                    )}
                    &nbsp;&nbsp;
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Minimum WEAK password strength
                    </Text>
                  </div>
                </div>
              </Form.Item>
              <Form.Item
                label={screens.md ? "Retype password" : ""}
                name="retypepassword"
                dependencies={["password"]}
                rules={[
                  {},
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value && getFieldValue("password")) {
                        return Promise.reject(
                          new Error(
                            "The new password that you entered do not match!"
                          )
                        );
                      } else if (!value) {
                        return Promise.resolve();
                      } else if (getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          "The new password that you entered do not match!"
                        )
                      );
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Retype password" />
              </Form.Item>
              <Form.Item
                name="accepttnc"
                valuePropName="checked"
                rules={[
                  { required: true, message: "" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (value) return Promise.resolve();
                      return Promise.reject(
                        new Error("Please check the checkbox to continue.")
                      );
                    },
                  }),
                ]}
              >
                <Checkbox>
                  By creating an account, you agree to our{" "}
                  <Link
                    href="https://www.virtualtkd.gg/terms-of-service"
                    target="_blank"
                  >
                    Terms of Service
                  </Link>{" "}
                  &amp;{" "}
                  <Link
                    href="https://www.virtualtkd.gg/privacy-policy"
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>
                  .
                </Checkbox>
              </Form.Item>
              <Form.Item style={{ margin: 0 }}>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  loading={loadingLogin}
                  disabled={
                    !/^(?=.*[a-zA-Z])(?=.*[0-9]).+$/.test(newPW) ||
                    newPW.length < 8 ||
                    passwordScore < 2
                  }
                >
                  Set Password
                </Button>
              </Form.Item>
            </Form>

            <div style={{ textAlign: "center", marginTop: 12 }}>
              <Button type="link" onClick={resetLoginPage}>
                Back to Login
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
